
import React, { useState, useEffect } from "react";
import { useLocation, useSearchParams, Link } from "react-router-dom";
import BrowseHeader from "@/components/browse/BrowseHeader";
import AgentGrid from "@/components/browse/AgentGrid";
import BrowseTabs from "@/components/browse/BrowseTabs";
import LoadingAgents from "@/components/browse/LoadingAgents";
import { Agent } from "@/models/agent";
import { AgentService } from "@/services/agent-service";
import { Button } from "@/components/ui/button";
import { Grid2X2 } from "lucide-react";

const Browse: React.FC = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState<string>("all");

  // Set the current tab based on URL params
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab) {
      setCurrentTab(tab);
    } else {
      setCurrentTab("all");
    }
  }, [searchParams]);

  // Load agents
  useEffect(() => {
    const loadAgents = async () => {
      setLoading(true);
      try {
        const data = await AgentService.getAgents();
        setAgents(data);
      } catch (error) {
        console.error("Failed to load agents:", error);
      } finally {
        setLoading(false);
      }
    };

    loadAgents();
  }, []);

  // Filter agents based on search, category, and current tab
  const filteredAgents = agents.filter(agent => {
    const matchesSearch = searchQuery === "" || 
      agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      agent.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = !selectedCategory || agent.category === selectedCategory;
    
    // Tab-specific filtering
    let matchesTab = true;
    if (currentTab === "featured") {
      matchesTab = agent.downloads > 100; // Simple logic to determine featured status
    } else if (currentTab === "new") {
      // Show agents created in the last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      matchesTab = new Date(agent.createdAt) >= thirtyDaysAgo;
    } else if (currentTab === "popular") {
      matchesTab = agent.downloads > 50; // Simple logic for popular agents
    }
    
    return matchesSearch && matchesCategory && matchesTab;
  });

  // Handle search
  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  // Handle category selection
  const handleCategoryChange = (category: string | null) => {
    setSelectedCategory(category);
  };

  return (
    <div className="container mx-auto px-4 py-8 min-h-screen">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <BrowseHeader searchQuery={searchQuery} onSearch={handleSearch} />
        <Button variant="outline" asChild className="w-full md:w-auto">
          <Link to="/categories" className="flex items-center justify-center gap-2">
            <Grid2X2 className="h-4 w-4" />
            View Categories
          </Link>
        </Button>
      </div>
      
      <div className="my-6 overflow-x-auto scrollbar-hide -mx-4 px-4">
        <BrowseTabs currentTab={currentTab} />
      </div>
      
      {loading ? (
        <LoadingAgents />
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-6">
            <h2 className="text-xl font-semibold">
              {currentTab === "all" && "All Agents"}
              {currentTab === "featured" && "Featured Agents"}
              {currentTab === "new" && "New Releases"}
              {currentTab === "popular" && "Popular Agents"}
            </h2>
            <div className="text-sm text-gray-500">
              {filteredAgents.length} {filteredAgents.length === 1 ? "agent" : "agents"} found
            </div>
          </div>
          
          <AgentGrid agents={filteredAgents} />
          
          {/* Pagination - can be implemented later */}
          <div className="mt-12 flex justify-center">
            <div className="text-center text-gray-500">
              Showing all {filteredAgents.length} results
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Browse;
